import { useState, useEffect } from "react";
import { Link } from "wouter";
import { CircleCheck } from "lucide-react";

type Vertical = "restaurant" | "hospitality";

const featuresCopy: Record<Vertical, Array<{ verb: string; rest: string }>> = {
  restaurant: [
    { verb: "Risponde", rest: "ai tuoi clienti su WhatsApp 24/7" },
    { verb: "Gestisce", rest: "le prenotazioni in automatico" },
    { verb: "Invia", rest: "reminder automatici per le prenotazioni" },
    { verb: "Risponde", rest: "a domande su menu, allergeni, orari e servizi" },
    { verb: "Invia", rest: "promozioni ed eventi ai tuoi contatti" },
    { verb: "Aumenta", rest: "le recensioni positive in automatico" },
    { verb: "Raccoglie", rest: "nuovi clienti automaticamente" },
    { verb: "Tutte", rest: "le richieste organizzate in una dashboard" },
  ],
  hospitality: [
    { verb: "Risponde", rest: "ai tuoi clienti su WhatsApp 24/7" },
    { verb: "Gestisce", rest: "richieste e prenotazioni camere" },
    { verb: "Invia", rest: "reminder e campagne marketing per promozioni o eventi" },
    { verb: "Risponde", rest: "a domande su check-in, check-out e altri servizi" },
    { verb: "Raccoglie", rest: "esigenze particolari (bambini, animali, transfer)" },
    { verb: "Aumenta", rest: "le recensioni positive in automatico" },
    { verb: "Raccoglie", rest: "nuovi clienti automaticamente" },
    { verb: "Tutte", rest: "le richieste organizzate in una dashboard" },
  ],
};

const pricingCopy: Record<Vertical, { original: string; discounted: string; monthly: string }> = {
  restaurant: { original: "€390", discounted: "€140", monthly: "€90" },
  hospitality: { original: "€390", discounted: "€140", monthly: "€190" },
};

const FAIR_END = new Date("2026-03-17T23:59:59");

function useCountdown(target: Date) {
  const getTimeLeft = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);
  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);
  return timeLeft;
}

function useVertical(): Vertical {
  const params = new URLSearchParams(window.location.search);
  return params.get("vertical") === "hospitality" ? "hospitality" : "restaurant";
}

function OfferCard({
  original,
  discounted,
  monthly,
  dark = false,
}: {
  original: string;
  discounted: string;
  monthly: string;
  dark?: boolean;
}) {
  return (
    <div
      className={`w-full max-w-sm rounded-2xl border-2 p-6 flex flex-col items-center gap-4 ${
        dark ? "border-white/20 bg-white/5" : "border-gray-200 bg-white"
      }`}
    >
      <p className="text-xs font-bold tracking-widest text-[#E07833] uppercase">OFFERTA FIERA</p>
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-base line-through text-gray-400">{original}</span>
        <span className={`text-5xl font-black leading-none ${dark ? "text-white" : "text-[#1B2B5E]"}`}>
          {discounted}
        </span>
        <span className={`text-sm ${dark ? "text-gray-300" : "text-gray-500"}`}>solo una volta</span>
      </div>
      <div className="w-full rounded-xl bg-[#E8F5EE] py-4 px-3 flex flex-col items-center gap-1">
        <span className="text-2xl font-black text-[#2A8C55]">Primo mese</span>
        <span className="text-2xl font-black text-[#2A8C55]">GRATIS</span>
        <span className="text-xs text-[#2A8C55]">provi senza impegno</span>
      </div>
      <p className={`text-sm ${dark ? "text-gray-300" : "text-gray-600"}`}>
        Solo se ti piace, paghi <strong>{monthly}/mese</strong>
      </p>
    </div>
  );
}

export function LandingPage() {
  const vertical = useVertical();
  const features = featuresCopy[vertical];
  const pricing = pricingCopy[vertical];
  const registerHref = `/register?vertical=${vertical}`;
  const { days, hours, minutes, seconds } = useCountdown(FAIR_END);
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="flex flex-col w-full min-h-screen">
      {/* Announcement bar 1 */}
      <div className="w-full bg-gray-100 text-center text-sm text-gray-600 py-2.5 px-4">
        Benvenuto! Hai appena provato la demo dell'assistente AI.
      </div>

      {/* Hero */}
      <section className="flex flex-col items-center gap-6 px-4 py-10 text-center">
        <h1 className="text-3xl font-black text-[#1B2B5E] leading-tight max-w-sm">
          Un assistente AI che lavora per te, 24 ore su 24
        </h1>
        <p className="text-base text-gray-500 max-w-xs">
          Attiva oggi con l'offerta esclusiva fiera e scopri cosa può fare per te.
        </p>

        {/* Countdown */}
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs font-bold text-gray-500 tracking-widest flex items-center gap-1.5">
            ⏰ L'OFFERTA SCADE TRA
          </p>
          <div className="flex gap-2">
            {[
              { value: pad(days), label: "GIORNI" },
              { value: pad(hours), label: "ORE" },
              { value: pad(minutes), label: "MIN" },
              { value: pad(seconds), label: "SEC" },
            ].map(({ value, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center bg-[#1B2B5E] text-white rounded-lg w-16 h-16"
              >
                <span className="text-2xl font-black leading-none">{value}</span>
                <span className="text-[9px] font-semibold mt-0.5 opacity-70 tracking-wide">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <OfferCard {...pricing} />

        {/* CTAs */}
        <div className="flex flex-col gap-3 w-full max-w-sm">
          <Link
            href={registerHref}
            className="w-full py-4 rounded-xl bg-[#2E9E5E] hover:bg-[#27895A] text-white font-bold text-base flex items-center justify-center gap-2 transition-colors shadow-[0_0_18px_rgba(46,158,94,0.45)]"
          >
            🟢 Attiva ora l'offerta fiera
          </Link>
          <Link
            href={`${registerHref}&lock=true`}
            className="w-full py-4 px-6 text-sm rounded-xl bg-[#E86830] hover:bg-[#D05A25] text-white font-bold text-base flex items-center justify-center gap-2 transition-colors shadow-[0_0_18px_rgba(232,104,48,0.45)]"
          >
            🚀 Blocca l'offerta ora e parti quando vuoi
          </Link>
        </div>
      </section>

      {/* Announcement bar 2 */}
      <div className="w-full bg-gray-50 border-t border-b border-gray-100 text-center py-3 px-4">
        <p className="text-sm text-gray-600">
          Blocchi il prezzo fiera oggi, attivi il canale e il mese gratis quando sei pronto.
        </p>
        <p className="text-sm text-gray-500 font-semibold italic mt-0.5">
          Offerta riservata ai visitatori della fiera HORECA.
        </p>
      </div>

      {/* Features */}
      <section className="flex flex-col items-center gap-6 px-4 py-10">
        <h2 className="text-2xl font-black text-[#1B2B5E] text-center">
          Ecco cosa farà per il tuo locale
        </h2>
        <ul className="flex flex-col gap-4 w-full max-w-sm">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <CircleCheck className="text-[#2E9E5E] shrink-0 mt-0.5" size={22} />
              <span className="text-base text-gray-700">
                <strong>{f.verb}</strong> {f.rest}
              </span>
            </li>
          ))}
        </ul>
        <Link
          href={registerHref}
          className="px-8 py-3 rounded-xl bg-[#1B2B5E] hover:bg-[#152040] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(27,43,94,0.45)]"
        >
          Attiva l'assistente AI
        </Link>
      </section>

      {/* Messages section */}
      <section className="flex flex-col items-center gap-4 px-4 py-8 text-center">
        <h2 className="text-2xl font-black text-[#1B2B5E]">
          Quanti messaggi ricevi ogni giorno?
        </h2>
        <p className="text-sm text-gray-500 max-w-xs">
          Ogni giorno le attività nella ristorazione ricevono decine di richieste:
        </p>
        <div className="flex flex-wrap gap-2 justify-center max-w-xs">
          {["Prenotazioni", "Orari", "Allergie", "Menu", "Eventi", "Servizi"].map((tag) => (
            <span
              key={tag}
              className="px-4 py-2 rounded-full border border-gray-300 text-sm text-gray-600 font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="text-base text-gray-600 max-w-xs">
          Con <strong className="text-[#1899A8]">Helpami AI</strong> tutte queste richieste vengono gestite automaticamente.
        </p>
      </section>

      {/* Steps */}
      <section className="flex flex-col items-center gap-6 px-4 py-8 text-center">
        <h2 className="text-2xl font-black text-[#1B2B5E]">Attivarlo è semplicissimo</h2>
        <div className="grid grid-cols-3 gap-3 w-full max-w-sm">
          {[
            { num: "1", label: "Attiva l'offerta" },
            { num: "2", label: "Configura il servizio" },
            { num: "3", label: "L'AI risponde ai clienti" },
          ].map(({ num, label }) => (
            <div key={num} className="flex flex-col items-center gap-2 bg-gray-50 rounded-xl p-3">
              <span className="w-10 h-10 rounded-full bg-[#1899A8] text-white font-black text-lg flex items-center justify-center shrink-0">
                {num}
              </span>
              <span className="text-xs font-medium text-gray-700 text-center">{label}</span>
            </div>
          ))}
        </div>
        <Link
          href={registerHref}
          className="px-8 py-3 rounded-xl bg-[#2E9E5E] hover:bg-[#27895A] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(46,158,94,0.45)]"
        >
          Attiva ora la prova gratuita
        </Link>
      </section>

      {/* Risk-free */}
      <section className="flex flex-col items-center gap-4 px-4 py-10 bg-gray-50 text-center">
        <h2 className="text-2xl font-black text-[#1B2B5E]">Provalo senza rischi</h2>
        <ul className="flex flex-col gap-3">
          {["1 mese gratuito", "Nessun vincolo", "Puoi disattivarlo quando vuoi"].map((item) => (
            <li key={item} className="flex items-center gap-2 justify-center text-base text-gray-700">
              <CircleCheck className="text-[#2E9E5E] shrink-0" size={20} />
              {item}
            </li>
          ))}
        </ul>
        <Link
          href={registerHref}
          className="px-8 py-3 rounded-xl bg-[#2E9E5E] hover:bg-[#27895A] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(46,158,94,0.45)]"
        >
          Attiva il mese gratis
        </Link>
      </section>

      {/* Dark CTA */}
      <section className="flex flex-col items-center gap-6 px-4 py-10 bg-[#1B2B5E] text-center">
        <h2 className="text-2xl font-black text-white">
          Attiva ora l'assistente AI per il tuo locale
        </h2>
        <OfferCard {...pricing} dark />
        <div className="flex flex-col items-center gap-2 w-full max-w-sm">
          <Link
            href={registerHref}
            className="w-full py-4 rounded-xl bg-[#2E9E5E] hover:bg-[#27895A] text-white font-bold text-base flex items-center justify-center gap-2 transition-colors shadow-[0_0_18px_rgba(46,158,94,0.45)]"
          >
            🟢 Attiva l'offerta fiera
          </Link>
          <p className="text-xs text-gray-300">Promo valida solo per i visitatori della fiera.</p>
        </div>
        <div className="flex flex-col items-center gap-2 mt-2">
          <p className="text-sm text-gray-300">Vuoi riprovare la demo?</p>
          <a
            href={vertical === "hospitality" ? "https://wa.me/3901119821510" : "https://wa.me/393936320323"}
            className="px-6 py-3 rounded-xl border border-white/30 text-white font-bold text-sm flex items-center gap-2 hover:bg-white/10 transition-colors"
          >
            👉 Apri la demo WhatsApp
          </a>
          <p className="text-xs text-gray-400">Non serve registrazione.</p>
        </div>
      </section>
    </div>
  );
}
